import {useHovercardAttributesForIssue} from '@github-ui/hovercards/issue'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {BlockedIcon} from '@primer/octicons-react'
import {Link, Token} from '@primer/react'
import {graphql, useFragment} from 'react-relay'

import type {HeaderBlockedBySummary$key} from './__generated__/HeaderBlockedBySummary.graphql'
import styles from './HeaderBlockedBySummary.module.css'
import dividerLineStyles from './HeaderMetadataDivider.module.css'

export type HeaderBlockedBySummaryProps = {
  blockedBySecondaryKey?: HeaderBlockedBySummary$key
  size?: 'xlarge' | 'small'
}

export const HeaderBlockedBySummary = ({blockedBySecondaryKey, size = 'xlarge'}: HeaderBlockedBySummaryProps) => {
  const node = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment HeaderBlockedBySummary on Issue {
        state
        issueDependenciesSummary {
          blockedBy
        }
        blockedBy(first: 1, ranked: true) {
          nodes {
            titleHTML
            number
            url
            repository {
              name
              owner {
                login
              }
            }
          }
        }
      }
    `,
    blockedBySecondaryKey,
  )

  const {blockedBy: blockedByCount} = node?.issueDependenciesSummary ?? {}
  if (!node || !blockedByCount) return <></>
  if (node.state === 'CLOSED') return <></>

  const blockedByIssue = blockedByCount === 1 ? node?.blockedBy?.nodes?.[0] || undefined : undefined
  return (
    <>
      {size !== 'small' && <span className={dividerLineStyles.dividerLine} />}
      <HeaderBlockedBySummaryInternal blockedByCount={blockedByCount} size={size} blockedByIssue={blockedByIssue} />
    </>
  )
}

export type HeaderBlockedBySummaryInternalProps = {
  blockedByCount: number
  size: 'xlarge' | 'small'
  blockedByIssue?: {
    titleHTML: string
    number: number
    url: string
    repository: {
      name: string
      owner: {
        login: string
      }
    }
  }
}

function BlockedByIssueLink({
  blockedByIssue,
  size,
}: {
  blockedByIssue: NonNullable<HeaderBlockedBySummaryInternalProps['blockedByIssue']>
  size: 'xlarge' | 'small'
}) {
  const {titleHTML, number, url, repository} = blockedByIssue
  const hovercardAttrs = useHovercardAttributesForIssue({
    owner: repository.owner.login,
    repo: repository.name,
    issueNumber: number,
  })
  return (
    <Link
      className={styles.issueLink}
      aria-label={`Blocked by issue ${titleHTML}`}
      inline
      muted
      href={url}
      {...hovercardAttrs}
    >
      {size === 'small' ? (
        `#${number}`
      ) : (
        // Server-sanitized HTML from GraphQL titleHTML field
        // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string
        <VerifiedHTMLText html={titleHTML as SafeHTMLString} className="markdown-title" />
      )}
    </Link>
  )
}

export function HeaderBlockedBySummaryInternal({
  blockedByCount,
  size,
  blockedByIssue,
}: HeaderBlockedBySummaryInternalProps) {
  const label = `Blocked by ${blockedByCount} issue${blockedByCount === 1 ? '' : 's'}`

  const blockedByIssueLink = blockedByIssue ? (
    <BlockedByIssueLink blockedByIssue={blockedByIssue} size={size} />
  ) : undefined

  if (size === 'small') {
    return (
      <div className={styles.smallSummary}>
        <BlockedIcon className="fgColor-danger" size={14} />
        <span className={styles.visualLabel}>{blockedByIssueLink || blockedByCount}</span>
        <span className="sr-only">{label}</span>
      </div>
    )
  }

  const leadingText = `Blocked by${blockedByIssueLink ? ':' : ''}`
  return (
    <Token
      text={
        <>
          <span className={styles.visualLabel}>
            {leadingText} {blockedByIssueLink || blockedByCount}
          </span>
          <span className="sr-only">{label}</span>
        </>
      }
      leadingVisual={() => <BlockedIcon className="fgColor-danger" size={14} />}
      className={styles.token}
      size="xlarge"
    />
  )
}
